import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEO from "@/components/SEO";
import EmailLink from "@/components/EmailLink";
import AreasServed from "@/components/AreasServed";
import HeroSection from "@/components/home/HeroSection";
import QuickPaths from "@/components/home/QuickPaths";
import SubstackCTA from "@/components/SubstackCTA";

import ContactSection from "@/components/home/ContactSection";
import DifferentByDesign from "@/components/home/DifferentByDesign";
import ServicesOutcome from "@/components/home/ServicesOutcome";
import PracticeCredibility from "@/components/home/PracticeCredibility";
import FamilyReviews, { buildReviewSchema } from "@/components/home/FamilyReviews";
import ServiceAreaMap from "@/components/ServiceAreaMap";
import MissionSection from "@/components/home/MissionSection";
import FounderSection from "@/components/home/FounderSection";
import DigitalProductsPromo from "@/components/shop/DigitalProductsPromo";
import KeepExploring from "@/components/home/KeepExploring";
import ReferralOffer from "@/components/home/ReferralOffer";

const faqs = [
  { q: "What is in-home companion care?", a: "In-home companion care is non-medical support delivered in an older adult's own home — companionship and conversation, transportation to appointments and errands, meal preparation, light household help, medication reminders, and respite for family caregivers. It does not include nursing or hands-on clinical care." },
  { q: "What does a senior companion caregiver do?", a: "A senior companion caregiver provides non-medical, day-to-day support for an older adult at home: steady companionship and conversation, rides to appointments and errands, meal preparation, light housekeeping, medication reminders, encouragement to stay active, and regular updates to the family. A senior caregiver does not provide nursing or hands-on clinical care." },
  { q: "What does private senior care in Nashville include?", a: "Private senior care means the family arranges and pays for care directly rather than going through insurance or a public program. With Kinship Care Collective that includes a matched companion concierge, a single point of contact, and a care plan built around your loved one's routine across Nashville, Brentwood, Franklin, and Belle Meade." },
  { q: "Is Kinship Care Collective a children's or foster kinship care service?", a: "No. Despite the name, Kinship Care Collective serves older adults and their families. We provide non-medical senior and elder care in the home — companionship, transportation, meals, and daily support. We do not provide childcare, foster care, or kinship placement services for children." },

  { q: "How much do your services cost?", a: "Instead of one-size-fits-all rates, we build a personalized care plan around the level of support your family truly needs. Our pricing is transparent, all-inclusive, and tailored after a brief conversation and care assessment — no hidden fees, no surprises." },
  { q: "Are your companions certified or trained?", a: "Every companion concierge is personally vetted, background-checked, and reference-verified before joining our team. We also provide ongoing training to ensure our standards are consistently met." },
  { q: "Do you provide medical or nursing care?", a: "Kinship Care Collective provides non-medical companion and life support services only. We are a complement to — never a replacement for — your medical team. We can help coordinate with healthcare providers as needed." },
  { q: "How quickly can services begin?", a: "In most cases, we can begin within 48–72 hours of your initial consultation. For urgent needs, please call us directly and we will do our best to accommodate." },
  { q: "What areas of Nashville do you serve?", a: "We serve Nashville and its surrounding communities — including Brentwood, Franklin, Belle Meade, Mt. Juliet, and Murfreesboro — with the same standard of excellence, wherever you call home." },
  { q: "Will I have the same companion concierge each time?", a: "Yes. Continuity is central to the Kinship Standard. You'll be matched with a dedicated companion concierge — and a single point of contact who knows your family and anticipates your needs." },
  { q: "How do you match companions with clients?", a: "We start with a conversation — about your loved one's personality, interests, routines, and what a good day looks like for them. From there, we pair them with a companion who's a genuine fit, not just whoever's available. If the match isn't right, we adjust until it is." },
  { q: "What if a match doesn't feel right?", a: "Your comfort comes first. If a match doesn't feel right, we'll quietly arrange a new companion concierge at no additional cost." },
];

const Home = () => {
  return (
    <div>
      <SEO
        title="Senior Caregivers & Companion Care in Nashville TN | Kinship"
        description="Senior companion caregivers in Nashville, Brentwood & Franklin. Non-medical in-home senior care: companionship, rides, meals and respite. Free consultation."


        path="/"
        faqSchema={faqs.map((f) => ({ question: f.q, answer: f.a }))}
        extraJsonLd={[buildReviewSchema()].filter(Boolean) as Array<Record<string, unknown>>}
      />

      <HeroSection />
      <QuickPaths />
      <DifferentByDesign />

      <ServicesOutcome />
      <PracticeCredibility />
      <FamilyReviews />
      <MissionSection />
      <DigitalProductsPromo />
      <FounderSection />
      <ReferralOffer />
      <AreasServed />
      <ServiceAreaMap />
      <ContactSection />

      {/* FAQ */}
      <section className="py-24 md:py-32 bg-card">
        <div className="container">
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-warm-ink text-center mb-4 font-medium">Common Questions</p>
          <h2 className="text-3xl md:text-5xl font-heading font-semibold text-center mb-6">Frequently Asked</h2>
          <div className="w-16 h-px bg-warm mx-auto mb-16" />
          <div className="max-w-2xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-background border border-border rounded-xl px-6">
                  <AccordionTrigger className="text-left font-medium hover:no-underline">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <KeepExploring />

      <section className="py-14 md:py-20 bg-secondary/40">
        <div className="container max-w-4xl">
          <SubstackCTA variant="card" location="homepage_after_explore" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-primary">
        <div className="container text-center max-w-3xl mx-auto">
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-warm mb-4 font-medium">A Private Conversation</p>
          <h2 className="text-3xl md:text-5xl font-heading font-semibold text-primary-foreground mb-6">Your Life. Your Home. Your Standards.</h2>
          <div className="w-16 h-px bg-warm mx-auto mb-8" />
          <p className="text-primary-foreground/85 mb-10 text-lg md:text-xl font-light leading-relaxed">
            Let's talk about what exceptional support looks like for you — or explore our <Link to="/senior-companion-care-nashville" className="underline text-primary-foreground hover:text-primary-foreground">senior companion care in Nashville</Link> to learn more.
          </p>
          <p className="text-primary-foreground/60 text-sm mb-10">
            <a href="tel:+16158070618" className="underline text-primary-foreground/85 hover:text-primary-foreground">(615) 807-0618</a> · <EmailLink className="underline text-primary-foreground/85 hover:text-primary-foreground" />
          </p>
          <Button asChild size="xl" className="bg-warm text-warm-foreground hover:bg-warm/90 h-auto w-full sm:w-auto whitespace-normal py-4 text-center">
            <Link to="/contact">Schedule Your Private Consultation</Link>
          </Button>
        </div>
      </section>

      <div className="h-14 md:hidden" />
    </div>
  );
};

export default Home;
